/**
 * AI 图片分析指南
 * 提供详细的 prompt 模板，指导 AI 如何分析邮件图片并输出结构化 Payload
 */

import ELEMENT_SCHEMAS from "./elementSchemas";

export const IMAGE_ANALYSIS_GUIDE_NAME = "email_image_analysis_guide";

export const IMAGE_ANALYSIS_GUIDE_DESCRIPTION = `
指导 AI 如何分析邮件模板图片并输出符合 Easy Email Pro 格式的结构化 Payload。
系统使用 BlockManager.getBlockByType(type).create(payload) 创建元素。
`;

export const IMAGE_ANALYSIS_GUIDE_PROMPT = `
# 邮件模板图片分析指南

你是一个专业的邮件模板分析专家。请仔细分析用户提供的邮件截图，并按照以下规范输出结构化的 JSON Payload。

## ⚠️ 核心规则：每个元素必须包含 type 和 data 字段！

\`\`\`json
{
  "type": "standard-xxx",  // ⚠️ 必须！
  "data": {},              // ⚠️ 必须！（通常为空对象）
  "attributes": {...},
  "children": [...]
}
\`\`\`

### 强制要求
1. **Page**: \`"type": "page"\`
2. **Section**: \`"type": "standard-section"\`
3. **Column**: \`"type": "standard-column"\`
4. **所有元素**: 必须有 \`type\` 和 \`data\` 字段
5. **属性使用 kebab-case**: 如 "background-color"、"padding-top"
6. **children 是数组**: 文本内容使用 \`[{ "text": "内容" }]\`

---

## 结构层级

\`\`\`
page (type: "page")
└── sections (数组)
    └── Section (type: "standard-section")
        └── children (列数组)
            └── Column (type: "standard-column")
                └── children (元素数组)
                    └── Element (type: "standard-xxx")
                        └── children (TextNode 数组)
                            └── TextNode ({ text: "内容", bold?: true, ... })
\`\`\`

---

## 分析步骤

### 第一步：分析页面配置 (Page)

\`\`\`json
{
  "page": {
    "type": "page",
    "attributes": {
      "width": "600px",
      "background-color": "#f5f5f5",
      "content-background-color": "#FFFFFF"
    },
    "data": {
      "breakpoint": "480px",
      "globalAttributes": { "font-family": "Arial, sans-serif" },
      "fonts": [],
      "preheader": ""
    }
  }
}
\`\`\`

### 第二步：从上到下识别区块 (Section)

⚠️ **所有 standard 元素的 padding 必须显式写出**（包括 Section、Image、Paragraph、Button、H1-H4 等）

每个区块的结构：

\`\`\`json
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "background-color": "#FFFFFF",
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [
    // Column 数组
  ]
}
\`\`\`

### 第三步：判断是否需要 Wrapper（容器）⚠️ 重要！

**当多个 Section 在视觉上属于同一个容器/分组时，应该使用 Wrapper 包裹它们！**

#### 何时使用 Wrapper

使用 Wrapper 的场景：
- 多个相关的 Section 在视觉上属于同一个容器
- 例如：两个内容块（顶部和底部）属于同一个视觉分组
- 需要为多个 Section 设置统一的背景色、padding 等样式

不使用 Wrapper 的场景：
- 每个 Section 都是独立的，没有明显的容器关系
- 通常按行（Section）逐个生成即可

#### Wrapper 结构

\`\`\`json
{
  "type": "standard-wrapper",
  "data": {},
  "attributes": {
    "background-color": "#F5F5F5",
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [
    // ⚠️ 注意：Wrapper 的 children 是 Section 数组（不是 Column 数组）
    {
      "type": "standard-section",
      "data": {},
      "attributes": { ... },
      "children": [ /* Column 数组 */ ]
    },
    {
      "type": "standard-section",
      "data": {},
      "attributes": { ... },
      "children": [ /* Column 数组 */ ]
    }
  ]
}
\`\`\`

⚠️ **重要区别**：
- **Section** 的 children 是 **Column 数组**
- **Wrapper** 的 children 是 **Section 数组**

### 第四步：分析列 (Column) 布局

\`\`\`json
{
  "type": "standard-column",
  "data": {},
  "attributes": {
    "width": "100%",
    "vertical-align": "top"
  },
  "children": [
    // 元素数组
  ]
}
\`\`\`

常用宽度：
- 单列: "100%"
- 两列: "50%" + "50%" 或 "40%" + "60%"
- 三列: "33.33%" + "33.33%" + "33.33%"

### 第五步：识别文本对齐方式 ⚠️ 重要！

**必须准确识别文本的对齐方式**，这是影响视觉效果的关键属性！

#### 如何判断对齐方式

1. **left（左对齐）**：
   - 文本从左侧开始，右侧不整齐
   - 常见于：段落文本、多行文本、左列内容
   - 视觉特征：文本左边缘对齐，右边缘参差不齐

2. **center（居中）**：
   - 文本在容器中居中显示
   - 常见于：标题、Hero 区块内容、按钮
   - 视觉特征：文本左右两侧都有空白，文本居中

3. **right（右对齐）**：
   - 文本从右侧结束，左侧不整齐
   - 常见于：某些特殊布局、RTL 语言
   - 视觉特征：文本右边缘对齐，左边缘参差不齐

#### 常见场景示例

\`\`\`json
// ✅ Hero 区块：标题和段落通常居中
{
  "type": "standard-h1",
  "attributes": {
    "align": "center"  // ✅ 居中
  }
}

// ✅ 正文段落：通常左对齐或居中
{
  "type": "standard-paragraph",
  "attributes": {
    "align": "left"  // ✅ 左对齐（常见）
  }
}

// ✅ 按钮：根据设计可能是居中或左对齐
{
  "type": "standard-button",
  "attributes": {
    "align": "center"  // ✅ 居中（常见）
  }
}

// ✅ 两列布局：左列文本通常左对齐，右列文本通常左对齐
{
  "type": "standard-column",
  "attributes": { "width": "50%" },
  "children": [{
    "type": "standard-paragraph",
    "attributes": {
      "align": "left"  // ✅ 左对齐
    }
  }]
}
\`\`\`

#### 常见错误 ❌

\`\`\`json
// ❌ 错误：误判为居中，实际是左对齐
{
  "type": "standard-paragraph",
  "attributes": {
    "align": "center"  // ❌ 错误！应该根据实际视觉判断
  }
}

// ❌ 错误：所有文本都设为 left，忽略了居中的标题
{
  "type": "standard-h2",
  "attributes": {
    "align": "left"  // ❌ 错误！Hero 区块的标题通常是 center
  }
}
\`\`\`

#### 判断技巧

1. **观察文本在容器中的位置**：如果文本左右都有大致相等的空白 → center
2. **观察文本起始位置**：如果文本从容器最左侧开始 → left
3. **观察文本结束位置**：如果文本在容器最右侧结束 → right
4. **参考上下文**：Hero 区块通常居中，正文段落通常左对齐
5. **对比多行文本**：如果多行文本的左侧边缘对齐 → left；如果每行都居中 → center
6. **深色背景上的文字**：通常左对齐或居中，很少右对齐

⚠️ **特别注意**：不要假设所有文本都是相同的对齐方式！每个元素都要单独判断！

---

### 第五步：识别背景色和文字颜色 ⚠️ 关键！

**必须准确识别背景色，并设置对应的文字颜色！**

#### 背景色识别规则

1. **深色背景（红、黑、深灰等）** → 文字必须是**白色或浅色**
   - 红色背景 → 白色文字
   - 黑色背景 → 白色文字
   - 深蓝色背景 → 白色文字

2. **浅色背景（白、浅灰等）** → 文字必须是**黑色或深色**
   - 白色背景 → 黑色/深灰文字
   - 浅灰背景 → 黑色文字

#### 示例

\`\`\`json
// ✅ 红色背景 + 白色文字
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "background-color": "#FF0000",  // 红色背景
    "padding-top": "40px",
    "padding-bottom": "40px",
    "padding-left": "20px",
    "padding-right": "20px"
  },
  "children": [
    {
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "100%" },
      "children": [
        {
          "type": "standard-h1",
          "data": {},
          "attributes": {
            "color": "#FFFFFF",  // ⚠️ 白色文字！
            "align": "left"
          },
          "children": [{ "text": "CYBER MONDAY DEALS" }]
        },
        {
          "type": "standard-paragraph",
          "data": {},
          "attributes": {
            "color": "#FFFFFF",  // ⚠️ 白色文字！
            "align": "left"
          },
          "children": [
            { "text": "There's still time to save!\\nUp to 65% off while supplies last.\\n" },
            {
              "text": "Browse Deals",
              "color": "#FFFFFF",
              "underline": true  // 链接通常有下划线
            }
          ]
        }
      ]
    }
  ]
}
\`\`\`

---

### 第六步：识别多行标题和特殊字符

#### 多行标题

如果标题在视觉上分为多行，使用换行符：

\`\`\`json
{
  "type": "standard-h1",
  "data": {},
  "attributes": { "color": "#FFFFFF" },
  "children": [
    { "text": "CYBER\\nMONDAY\\nDEALS" }  // 使用 \\n 换行
  ]
}
\`\`\`

#### 按钮中的特殊字符

**必须保留按钮文本中的所有特殊字符**（箭头、符号等）：

\`\`\`json
{
  "type": "standard-button",
  "data": { "content": "Button" },
  "attributes": {
    "background-color": "#FF0000",
    "color": "#FFFFFF"
  },
  "children": [
    { "text": "See More Savings →" }  // ⚠️ 箭头必须保留
  ]
}
\`\`\`

---

### 第七步：识别左右分栏布局

**识别左右分栏时，注意列宽比例**：

\`\`\`json
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "20px",
    "padding-right": "20px"
  },
  "children": [
    {
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "50%" },  // 左侧文字
      "children": [
        {
          "type": "standard-h3",
          "data": {},
          "attributes": { "align": "left" },
          "children": [{ "text": "New deals added\\nevery hour." }]
        },
        {
          "type": "standard-button",
          "data": { "content": "Button" },
          "attributes": {
            "background-color": "#FF0000",
            "color": "#FFFFFF"
          },
          "children": [{ "text": "See More Savings →" }]
        }
      ]
    },
    {
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "50%" },  // 右侧图片
      "children": [
        {
          "type": "standard-image",
          "data": {},
          "attributes": {
            "src": "https://images.unsplash.com/photo-xxx?w=300&h=300&fit=crop",
            "width": "300px",
            "padding-top": "0px",
            "padding-left": "0px",
            "padding-right": "0px",
            "padding-bottom": "0px"
          },
          "children": [{ "text": "" }]
        }
      ]
    }
  ]
}
\`\`\`

---

${ELEMENT_SCHEMAS}

---

## 输出格式

请输出以下 JSON 结构：

\`\`\`json
{
  "page": {
    "type": "page",
    "attributes": {
      "width": "600px",
      "background-color": "#f5f5f5"
    },
    "data": {
      "breakpoint": "480px"
    }
  },
  "sections": [
    // 方式 1：直接使用 Section（独立的内容块）
    {
      "type": "standard-section",
      "data": {},
      "attributes": {
        "background-color": "#FFFFFF",
        "padding-top": "20px",
        "padding-bottom": "20px",
        "padding-left": "0px",
        "padding-right": "0px"
      },
      "children": [
        {
          "type": "standard-column",
          "data": {},
          "attributes": { "width": "100%" },
          "children": [
            {
              "type": "standard-image",
              "data": {},
              "attributes": {
                "src": "https://images.unsplash.com/photo-xxx?w=600&h=400&fit=crop",
                "alt": "Detailed image description for AI generation with scene, objects, lighting, style [Width: 600px, Height: 400px]",
                "width": "600px",
                "align": "center",
                "padding-top": "0px",
                "padding-bottom": "10px",
                "padding-left": "0px",
                "padding-right": "0px"
              },
              "children": [{ "text": "" }]
            },
            {
              "type": "standard-paragraph",
              "data": {},
              "attributes": {
                "align": "center",
                "color": "#333333",
                "padding-top": "0px",
                "padding-bottom": "10px",
                "padding-left": "0px",
                "padding-right": "0px"
              },
              "children": [
                { "text": "Hello World" }
              ]
            }
          ]
        }
      ]
    },
    // 方式 2：使用 Wrapper 包裹多个相关的 Section（属于同一个容器）
    {
      "type": "standard-wrapper",
      "data": {},
      "attributes": {
        "background-color": "#F5F5F5",
        "padding-top": "30px",
        "padding-bottom": "30px",
        "padding-left": "0px",
        "padding-right": "0px"
      },
      "children": [
        {
          "type": "standard-section",
          "data": {},
          "attributes": {
            "background-color": "#FFFFFF",
            "padding-top": "20px",
            "padding-bottom": "20px",
            "padding-left": "0px",
            "padding-right": "0px"
          },
          "children": [ /* Column 数组 */ ]
        },
        {
          "type": "standard-section",
          "data": {},
          "attributes": {
            "background-color": "#FFFFFF",
            "padding-top": "20px",
            "padding-bottom": "20px",
            "padding-left": "0px",
            "padding-right": "0px"
          },
          "children": [ /* Column 数组 */ ]
        }
      ]
    }
  ]
}
\`\`\`

---

## 图片处理指南

### ⚠️ 重要规则

1. **width 只支持 px 单位或留空**（不支持百分比如 "100%" ❌）
2. **所有 standard 元素的 padding 必须显式写出**（Section、Image、Paragraph、Button、H1-H4、Divider 等, 除 column了）
3. **alt 必须详细描述**（用于 AI 图片生成）

### 使用 Unsplash 占位图片（支持宽高参数）

在生成模板时，图片使用 Unsplash 作为临时占位。**Unsplash 支持通过 URL 参数设置宽高**，确保占位图尺寸匹配！

#### URL 参数说明

Unsplash Source API 支持以下参数：
- \`w\`: 宽度（像素）
- \`h\`: 高度（像素）
- \`fit\`: 适应方式
  - \`fit=crop\`: **裁剪图片以完全填充指定尺寸**（推荐，确保尺寸精确匹配）
  - \`fit=crop\`: 在指定尺寸内最大化显示，保持原始比例（可能留白）

⚠️ **必须使用 \`fit=crop\`** 以确保占位图完全填充指定尺寸，避免尺寸不匹配！

#### URL 格式

根据 alt 描述中的宽高信息生成 URL：

\`\`\`
https://images.unsplash.com/photo-xxx?w=600&h=400&fit=crop
\`\`\`

#### 示例

\`\`\`json
{
  "type": "standard-image",
  "data": {},
  "attributes": {
    "src": "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600&h=400&fit=crop",
    "alt": "A modern minimalist workspace with a sleek laptop, wireless keyboard, and succulent plant on a white marble desk, natural lighting from large windows [Width: 600px, Height: 400px]",
    "width": "600px",
    "align": "center",
    "padding-top": "10px",
    "padding-bottom": "10px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [{ "text": "" }]
}
\`\`\`

⚠️ **重要**：URL 中的 \`w\` 和 \`h\` 参数必须与 alt 描述中的宽高信息一致！

### 全宽图片（留空 width）

\`\`\`json
{
  "type": "standard-image",
  "data": {},
  "attributes": {
    "src": "https://images.unsplash.com/photo-xxx?w=600&h=300&fit=crop",
    "alt": "Wide banner image description with detailed scene, objects, lighting, and style information [Width: 600px, Height: 300px]",
    "align": "center",
    "padding-top": "0px",
    "padding-bottom": "0px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [{ "text": "" }]
}
\`\`\`

### 如何从 alt 描述生成 URL

1. **从 alt 描述中提取宽高**：格式为 \`[Width: XXXpx, Height: XXXpx]\`
2. **构建 URL**：\`https://images.unsplash.com/photo-xxx?w=XXX&h=XXX&fit=crop\`
   - ⚠️ **必须使用 \`fit=crop\`** 以确保图片完全填充指定尺寸，避免尺寸不匹配
3. **示例**：
   - alt: \`"... [Width: 600px, Height: 400px]"\`
   - URL: \`https://images.unsplash.com/photo-xxx?w=600&h=400&fit=crop\`

### Alt 描述要求

**alt 描述要非常详细具体**，因为它将用于 AI 图片生成。**必须包含图片的宽高信息**，以确保生成的图片尺寸匹配！

#### 格式要求

alt 描述格式：\`"图片内容详细描述 [Width: XXXpx, Height: XXXpx]"\`

**必须包含**：
1. 图片内容的详细描述（场景、对象、风格、光线等）
2. **图片的宽高信息**（格式：\`[Width: XXXpx, Height: XXXpx]\`）

#### 好的描述示例（包含宽高）

\`\`\`json
{
  "type": "standard-image",
  "attributes": {
    "src": "https://images.unsplash.com/photo-xxx?w=600&h=400&fit=crop",
    "alt": "Professional business team in modern office, 4 diverse people collaborating around a white table, natural lighting, minimalist interior design [Width: 600px, Height: 400px]",
    "width": "600px"
  }
}

{
  "type": "standard-image",
  "attributes": {
    "src": "https://images.unsplash.com/photo-xxx?w=300&h=300&fit=crop",
    "alt": "Elegant product photography of skincare bottles on white marble surface, soft shadows, luxury cosmetic branding [Width: 300px, Height: 300px]",
    "width": "300px"
  }
}

{
  "type": "standard-image",
  "attributes": {
    "src": "https://images.unsplash.com/photo-xxx?w=600&h=300&fit=crop",
    "alt": "Delicious breakfast spread with golden pancakes, fresh berries, maple syrup, and coffee on rustic wooden table, morning sunlight [Width: 600px, Height: 300px]",
    "width": "600px"
  }
}
\`\`\`

#### 差的描述 ❌

\`\`\`json
// ❌ 缺少宽高信息
{
  "alt": "Team photo"
}

// ❌ 描述不够详细，且缺少宽高
{
  "alt": "Product image"
}

// ❌ 缺少宽高信息
{
  "alt": "Delicious burger on wooden board"
}
\`\`\`

#### 如何确定图片尺寸

1. **观察图片在邮件中的实际显示尺寸**
   - 全宽图片：通常是 600px 宽（邮件标准宽度）
   - 列内图片：根据列宽估算（如 50% 列宽 = 约 300px）
   - 固定尺寸图片：观察图片的实际像素尺寸

2. **根据 width 属性确定**
   - 如果 \`width: "600px"\` → 高度通常为 300-400px（横幅）或 600px（正方形）
   - 如果 \`width: "300px"\` → 高度通常为 200-300px
   - 如果 width 为空（全宽）→ 通常是 600px 宽

3. **观察图片宽高比**
   - 横幅图片：宽 > 高（如 600x300, 600x400）
   - 正方形：宽 = 高（如 300x300, 600x600）
   - 竖图：宽 < 高（较少见）

⚠️ **重要**：宽高信息必须准确，否则后续 AI 生成的图片尺寸不匹配会导致对齐问题！

### Hero 背景图

Hero 区块的背景图也需要详细的 alt 描述：

\`\`\`json
{
  "type": "standard-hero",
  "data": {},
  "attributes": {
    "background-url": "https://images.unsplash.com/photo-1451187580459-43490279c0fa",
    "alt": "Abstract blue technology background with glowing network connections and digital particles",
    "mode": "fluid-height",
    "background-color": "#1a1a2e",
    "padding-top": "40px",
    "padding-bottom": "40px",
    "padding-left": "20px",
    "padding-right": "20px"
  },
  "children": [...]
}
\`\`\`

---

## 常见错误检查清单

在生成 JSON 前，请检查：

1. ✅ **每个元素都有 type 字段吗？**（包括 Section 和 Column）
2. ✅ **每个元素都有 data 字段吗？**（通常为空对象 \`{}\`）
3. ✅ type 是否使用 "standard-xxx" 格式？
4. ✅ children 是否为数组？
5. ✅ TextNode 是否有 text 字段？
6. ✅ 属性是否使用 kebab-case（如 padding-top）？
7. ✅ 颜色值是否为有效的十六进制格式？
8. ✅ **文本元素的 align 属性是否正确**（left/center/right）？⚠️ 必须准确识别！
9. ✅ **图片的 alt 是否足够详细且包含宽高信息**（格式：\`"描述 [Width: XXXpx, Height: XXXpx]"\`）？
10. ✅ **图片 width 是否使用 px 单位**（不支持百分比）？
11. ✅ **所有 standard 元素的 padding 是否显式写出**（Section、Image、Paragraph、Button、H1-H4、Divider 等）？

---

## 图片对比和迭代修正流程 ⚠️ 重要！

生成 template JSON 后，**必须进行图片对比和迭代修正**，确保生成的模板与原图一致。

### 完整工作流程

#### 第一步：生成初始 Template JSON

根据图片分析，生成初始的 template JSON（使用 Unsplash 占位图）。

#### 第二步：转换为图片进行对比

1. **转换为 MJML**：使用 \`convert_to_mjml\` 工具
   \`\`\`json
   {
     "template": { /* 生成的 template JSON */ },
     "mode": "production"
   }
   \`\`\`

2. **转换为 HTML**：使用 \`convert_to_html\` 工具
   \`\`\`json
   {
     "mjml": "/* 上一步得到的 MJML */"
   }
   \`\`\`

3. **转换为 PNG 图片**：使用外部工具（如浏览器或 puppeteer）将 HTML 渲染为 PNG 图片
   - 设置 viewport 为 600px 宽（邮件模板标准宽度）
   - 等待页面完全加载
   - 截图保存为 PNG

#### 第三步：对比两张图片

**原图 vs 生成的图片**，仔细对比以下方面：

##### 1. 布局差异
- **元素位置**：元素是否在正确的位置？
- **对齐方式**：文本、图片、按钮的对齐是否正确？
  - 左对齐：文本从容器最左侧开始
  - 居中：文本左右两侧空白大致相等
  - 右对齐：文本在容器最右侧结束
- **列宽比例**：多列布局的宽度比例是否正确？（如 33% + 67%）

##### 2. 样式差异
- **颜色**：文本颜色、背景颜色、按钮颜色是否正确？
- **字体大小**：标题、正文的字体大小是否匹配？
- **间距**：padding 和 margin 是否正确？
  - Section 之间的间距
  - 元素内部的 padding
  - 列之间的间距

##### 3. 内容差异
- **文本内容**：文本是否完全一致？
- **图片尺寸**：图片的宽高是否匹配？
- **图片占位**：是否使用了 Unsplash 占位图（而不是真实图片）？

##### 4. 结构差异
- **缺少元素**：是否遗漏了某些元素？
- **多余元素**：是否添加了不存在的元素？
- **嵌套结构**：Section、Column、Element 的嵌套是否正确？

#### 第四步：修正 JSON

根据识别出的差异，修正 template JSON：

##### 修正间距问题
\`\`\`json
// 调整 padding 修正间距
{
  "type": "standard-section",
  "attributes": {
    "padding-top": "20px",    // 增加/减少以匹配原图
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  }
}
\`\`\`

##### 修正对齐问题
\`\`\`json
// 调整 align 修正对齐
{
  "type": "standard-paragraph",
  "attributes": {
    "align": "left"  // 改为 "center" 或 "right" 以匹配原图
  }
}
\`\`\`

##### 修正列宽问题
\`\`\`json
// 调整 width 修正列宽
{
  "type": "standard-column",
  "attributes": {
    "width": "33%"  // 调整为正确的比例
  }
}
\`\`\`

##### 修正颜色问题
\`\`\`json
// 调整颜色以匹配原图
{
  "type": "standard-paragraph",
  "attributes": {
    "color": "#878282"  // 使用正确的颜色值
  }
}
\`\`\`

##### 修正结构问题
- **添加缺失的元素**：如果原图中有某个元素，但 JSON 中没有，需要添加
- **删除多余的元素**：如果 JSON 中有某个元素，但原图中没有，需要删除
- **调整嵌套结构**：确保 Section、Column、Element 的嵌套关系正确

#### 第五步：重复对比和修正

1. 使用修正后的 JSON 重新执行第二步（转换为图片）
2. 再次对比原图和生成的图片
3. 如果仍有差异，继续修正
4. **重复此过程直到两张图片完全一致**

### 参考模板学习

如果难以理解正确的结构，可以查看参考模板：

**参考模板位置**：\`src/train/template1/template.json\`

**学习要点**：
- 查看正确的结构格式（type、data、attributes、children）
- 学习 padding 的使用方式（所有 standard 元素都需要显式写出 padding，除 column 外）
- 学习图片占位的方式（使用 Unsplash URL，包含宽高参数）
- 学习文本格式化的方式（TextNode 数组，支持 bold、color、link 等）
- 学习列布局的方式（width 使用百分比，总和为 100%）

### 常见修正场景

#### 场景 1：文本对齐错误
**问题**：生成的图片中文本居中，但原图中文本左对齐
**修正**：将 \`"align": "center"\` 改为 \`"align": "left"\`

#### 场景 2：间距不匹配
**问题**：生成的图片中元素间距过大
**修正**：减少 Section 的 \`padding-top\` 和 \`padding-bottom\`

#### 场景 3：列宽比例错误
**问题**：两列布局中，左列太窄，右列太宽
**修正**：调整 Column 的 \`width\`，例如从 \`"50%" + "50%"\` 改为 \`"33%" + "67%"\`

#### 场景 4：颜色不匹配
**问题**：按钮颜色不对
**修正**：调整 Button 的 \`background-color\` 属性

#### 场景 5：缺少分隔线
**问题**：原图中有分隔线，但生成的 JSON 中没有
**修正**：添加 \`standard-divider\` 元素

### ⚠️ 重要提醒

1. **必须使用占位图**：生成的 template JSON 中，所有图片必须使用 Unsplash 占位图，不要使用真实图片 URL
2. **必须显式写出 padding**：所有 standard 元素的 padding 必须显式写出（除 column 外）
3. **必须准确识别对齐方式**：不要假设，要根据视觉效果准确判断
4. **必须迭代修正**：不要期望一次就生成完美的 JSON，需要多次对比和修正

---

请根据以上指南分析用户提供的邮件图片，并输出完整的结构 Payload JSON。生成后，务必进行图片对比和迭代修正，确保生成的模板与原图完全一致。
`;

export const imageAnalysisGuidePrompt = {
  name: IMAGE_ANALYSIS_GUIDE_NAME,
  description: IMAGE_ANALYSIS_GUIDE_DESCRIPTION,
  content: IMAGE_ANALYSIS_GUIDE_PROMPT,
};
